import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { Cookie, Shield, X, ChevronDown, ChevronUp, Settings } from 'lucide-react';
import { Switch } from './ui/switch';

// Export a function to open the cookie settings
let openCookieSettings = () => {};

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [isManaging, setIsManaging] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    functional: true,
    analytics: true,
    marketing: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      // Show banner after a short delay
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    } else {
      // Load saved preferences
      try {
        const saved = JSON.parse(consent);
        setPreferences({
          necessary: true,
          functional: saved.functional ?? true,
          analytics: saved.analytics ?? true,
          marketing: saved.marketing ?? false
        });
      } catch (e) {
        // Invalid saved data
      }
    }
  }, []);

  // Expose function to open settings from anywhere
  useEffect(() => {
    openCookieSettings = () => {
      setIsManaging(true);
      setShowDetails(true);
      setIsVisible(true);
    };
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookie_consent', JSON.stringify(allAccepted));
    setPreferences(allAccepted);
    setIsVisible(false);
    setIsManaging(false);
  };

  const handleRejectAll = () => {
    const allRejected = {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookie_consent', JSON.stringify(allRejected));
    setPreferences(allRejected);
    setIsVisible(false);
    setIsManaging(false);
  };

  const handleSavePreferences = () => {
    const savedPreferences = {
      ...preferences,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookie_consent', JSON.stringify(savedPreferences));
    setIsVisible(false);
    setIsManaging(false);
  };

  const cookieTypes = [
    {
      id: 'necessary',
      name: 'Strictly Necessary',
      description: 'Essential for the website to function. Cannot be disabled.',
      required: true
    },
    {
      id: 'functional',
      name: 'Functional',
      description: 'Remember your preferences and settings for a better experience.',
      required: false
    },
    {
      id: 'analytics',
      name: 'Analytics',
      description: 'Help us understand how visitors interact with our website.',
      required: false
    },
    {
      id: 'marketing',
      name: 'Marketing',
      description: 'Used to deliver relevant advertisements and track their effectiveness.',
      required: false
    }
  ];

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="fixed bottom-0 left-0 right-0 z-50 p-4"
        data-testid="cookie-consent-banner"
      >
        <div className="max-w-4xl mx-auto">
          <div className="bg-[hsl(220,20%,7%)]/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
            {/* Main Banner */}
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0">
                  {isManaging ? (
                    <Settings className="w-6 h-6 text-blue-400" />
                  ) : (
                    <Cookie className="w-6 h-6 text-blue-400" />
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="font-heading text-lg font-bold text-white mb-2">
                    {isManaging ? 'Manage Cookie Preferences' : 'We Value Your Privacy'}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {isManaging ? (
                      <>Update your cookie preferences below. Changes will take effect immediately.</>
                    ) : (
                      <>
                        We use cookies to enhance your browsing experience, provide personalized content, 
                        and analyze our traffic. By clicking "Accept All", you consent to our use of cookies. 
                        Read our{' '}
                        <a href="/privacy" className="text-blue-400 hover:underline">Privacy Policy</a>
                        {' '}for more information.
                      </>
                    )}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setIsVisible(false);
                    setIsManaging(false);
                  }}
                  className="text-gray-500 hover:text-white transition-colors p-1"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap items-center gap-3 mt-6">
                <Button
                  onClick={handleAcceptAll}
                  className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 font-heading uppercase tracking-wider"
                  data-testid="cookie-accept-all"
                >
                  Accept All
                </Button>
                <Button
                  variant="outline"
                  onClick={handleRejectAll}
                  className="font-heading uppercase tracking-wider border-white/10 text-white hover:bg-white/5"
                  data-testid="cookie-reject-all"
                >
                  Reject All
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setShowDetails(!showDetails)}
                  className="text-gray-400 hover:text-white ml-auto"
                >
                  {isManaging ? 'View Options' : 'Customize'}
                  {showDetails ? (
                    <ChevronUp className="w-4 h-4 ml-1" />
                  ) : (
                    <ChevronDown className="w-4 h-4 ml-1" />
                  )}
                </Button>
              </div>
            </div>

            {/* Cookie Preferences Details */}
            <AnimatePresence>
              {showDetails && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="border-t border-white/10 overflow-hidden"
                >
                  <div className="p-6 bg-white/5">
                    <div className="flex items-center gap-2 mb-4">
                      <Shield className="w-5 h-5 text-blue-400" />
                      <h4 className="font-heading font-bold text-white">Cookie Preferences</h4>
                    </div>
                    
                    <div className="space-y-4">
                      {cookieTypes.map((cookie) => (
                        <div
                          key={cookie.id}
                          className="flex items-center justify-between p-4 bg-[hsl(220,20%,10%)] rounded-lg border border-white/5"
                        >
                          <div className="flex-1 pr-4">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-white">{cookie.name}</span>
                              {cookie.required && (
                                <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded">
                                  Required
                                </span>
                              )}
                            </div>
                            <p className="text-gray-500 text-sm mt-1">{cookie.description}</p>
                          </div>
                          <Switch
                            checked={preferences[cookie.id]}
                            onCheckedChange={(checked) => 
                              !cookie.required && setPreferences(prev => ({ ...prev, [cookie.id]: checked }))
                            }
                            disabled={cookie.required}
                            className={cookie.required ? 'opacity-50' : ''}
                            data-testid={`cookie-toggle-${cookie.id}`}
                          />
                        </div>
                      ))}
                    </div>

                    <div className="flex justify-end mt-6">
                      <Button
                        onClick={handleSavePreferences}
                        className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 font-heading uppercase tracking-wider"
                        data-testid="cookie-save-preferences"
                      >
                        Save Preferences
                      </Button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export { openCookieSettings };
export default CookieConsent;
